import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import Button from 'src/components/controls/forms/redesign/FormButton';
import DocumentManagement from 'src/components/layout/DocumentManagement';
import DocumentViewer from 'src/components/layout/DocumentViewer';
import DialogList from 'src/dialogs';
import { DocumentTypes } from 'src/models/DocumentTypes';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import useFetch from 'use-http';
import ConfirmDeleteDocDialog from './ConfirmDeleteDocDialog';
import { useDispatch } from 'react-redux';
import actions from 'src/actions';
import GenerateDocs from './GenerateDocs';
import LeasePacketBuilder from './LeasePacketBuilder';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import LandContractType from 'src/models/LandContractType';
import S3FileUpload from 'src/models/leasing/S3FileUpload';
import ReportFileDetails from 'src/models/ReportFileDetails';

type fileDataType = {
    id: number;
    originalFileName: string;
    fileSize: number;
    fileExtension: string;
    fileType: string;
    s3FileId: number;
    s3KeyName: string;
    uploadDate: Date;
    note: string;
    docType: string;
    sourceIdentifier: string;
    expirationDate: Date;
    isPresigned?: boolean;
};

// TODO: Replace contract hold id with contract lookup Id
interface IAgreementDocsProps {
    showDialog?: (dialogName: string) => void;
    hideDialog?: () => void;
    generateLease?: (id: number) => void;
    generateLPR?: (id: number) => void;
    generateMemo?: (id: number) => void;
    generateOop?: (id: number) => void;
    refreshMORs?: () => void;
    updateOlderVersions?: (hasOlderVersion?: boolean) => void;
    isNewLease?: boolean;
    contextType?: LandContractType;
    contractLookupId?: number;
    leaseNumber?: number;
    filesToUpload?: ReportFileDetails[];
}

// let leaseTemplateOptions = [
//     { id: 0, name: 'Default Lease' },
//     { id: 1, name: 'Second Lease' }
// ];

const AgreementDocs = (props: IAgreementDocsProps) => {
    const appContext = useContext<IAppContextState>(AppContext);
    const [fileData, setFileData] = useState<S3FileUpload[]>(undefined);
    const [leaseFileData, setLeaseFileData] = useState<S3FileUpload[]>(undefined);
    const [loadingData, setLoadingData] = useState<boolean>(false);
    const [refreshFileList, setRefreshFileList] = useState<boolean>(false);
    const [fileIdToDelete, setFileIdToDelete] = useState(undefined);
    const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
    const [tractFilesToUpload, setTractFilesToUpload] = useState<ReportFileDetails[]>([]);
    const [docContext, setDocContext] = useState<string>('lease');
    const { get, request, response } = useFetch();
    const dispatch = useDispatch();

    useEffect(() => {
        switch (props.contextType) {
            case LandContractType.OilAndGas:
                setDocContext('lease');
                break;
            case LandContractType.SurfaceWaiver:
            case LandContractType.SolarLease:
            case LandContractType.BatteryStorage:
            case LandContractType.BrineLease:
                setDocContext('contract hold');
                break;
            default:
                break;
        }
    }, [props.contextType]);

    useEffect(() => {
        if (props?.filesToUpload?.length > 0) {
            setTractFilesToUpload(props.filesToUpload);
        }
    }, [props.filesToUpload]);

    useEffect(() => {
        if (leaseFileData?.length > 0 && loadingData) {
            setLoadingData(false);
            dispatch(actions.layout.showDialog(DialogList.LEASE_PACKET_BUILDER));
        }
    }, [leaseFileData]);

    useEffect(() => {
        if ((fileData || filesToUpload) && !loadingData && refreshFileList) {
            loadData();
            setRefreshFileList(false);
        }
    }, [refreshFileList]);

    const loadData = async () => {
        let filePath = ``;

        if (props.contextType === LandContractType.OilAndGas) {
            filePath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/s3bucket/agreements/${appContext.leaseId}`;
        } else {
            filePath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contract-holds/${props.contractLookupId}/s3bucket`;
        }

        const data = await get(filePath);
        if (response.ok) {
            setFileData(data);
        }
    };

    const getLeasePacket = async () => {
        setLoadingData(true);
        let filePath = '';

        if (props.contextType === LandContractType.OilAndGas) {
            filePath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/s3bucket/lease-packet/${appContext.leaseId}`;
        } else {
            let contractId;
            switch (props.contextType) {
                case LandContractType.SurfaceWaiver:
                    contractId = appContext.surfaceWaiverId;
                    break;
                case LandContractType.SolarLease:
                    contractId = appContext.solarLeaseId;
                    break;
                case LandContractType.BatteryStorage:
                    contractId = appContext.batteryStorageId;
                    break;
                case LandContractType.BrineLease:
                    contractId = appContext.brineLeaseId;
                    break;
                default:
                    break;
            }
            filePath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/s3bucket/contract-lease-packet/${props.contextType}/${contractId}`;
        }

        const data = await get(filePath);
        if (response.ok) {
            setLeaseFileData(data);
        }
    };

    useEffect(() => {
        if (props.contractLookupId > 0 || appContext.leaseId > 0) {
            loadData();
        }
    }, [props.contractLookupId]);

    // S3Identifier is the s3FileId for a presigned link, and s3Id for the normal link.
    const downloadFile = async (s3Identifier: string | number, index: number, fileName: string, presigned: boolean) => {
        let filepath = presigned ? `/v1.0/s3bucket/presigned/${s3Identifier}` : `/v1.0/s3bucket/${s3Identifier}`;

        await get(filepath);
        if (response.ok) {
            if (presigned) {
                window.alert('File is too large to download to browser. You should receive an email with a direct link to download.');
            } else {
                await response.blob().then((blob) => {
                    const blobUrl = window.URL.createObjectURL(blob);
                    const a = document.getElementById(`a_${index}`) as HTMLAnchorElement;
                    a.href = blobUrl;
                    a.download = fileName;
                    a.click();
                    window.URL.revokeObjectURL(blobUrl);
                });
            }
        }
    };

    const deleteFile = async (s3FileId: number) => {
        if (!isNaN(s3FileId)) {
            setFileIdToDelete(s3FileId);
            // props.showDialog(DialogList.DELETE_DOC);
            dispatch(actions.layout.showDialog(DialogList.DELETE_DOC));
        }
        // await request.delete(`/v1.0/s3bucket/${s3FileId}`);
        // loadData();
    };

    const deleteFileConfirmed = async () => {
        // props.hideDialog();
        dispatch(actions.layout.hideDialog());
        await request.delete(`/v1.0/s3bucket/${fileIdToDelete}`);
        setFileIdToDelete(undefined);
        loadData();
    };

    const deleteFileCanceled = () => {
        // props.hideDialog();
        dispatch(actions.layout.hideDialog());
        setFileIdToDelete(undefined);
    };

    const docType = (docTypeValue: string) => {
        const numDocType = parseInt(docTypeValue, 10);
        if (numDocType) {
            const type = DocumentTypes.find((dc) => dc.id === numDocType) || { name: 'Bad Doc Type' };
            return type;
        }
        return { name: 'No Doc Type Set' };
    };

    const displayFiles = () => {
        if (fileData) {
            return fileData.map((file: fileDataType, index: number) => {
                return (
                    <div
                        key={index}
                        style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid darkgrey' }}
                        className="mb-2"
                    >
                        <div style={{ width: '10%', textAlign: 'center' }}>{moment(file.uploadDate).format('MM-DD-YYYY')}</div>
                        <div style={{ width: '30%', textAlign: 'center' }}>{file.originalFileName}</div>
                        <div style={{ width: '10%', textAlign: 'center' }}>{file.fileSize / 1000 || 'NA'}Kb</div>
                        <div style={{ width: '10%', textAlign: 'center' }}>{docType(file.docType).name}</div>
                        <div style={{ width: '25%', textAlign: 'center' }}>{file.note}</div>
                        <div style={{ width: '15%', textAlign: 'center' }}>
                            <span
                                onClick={() => downloadFile(file.isPresigned ? file.id : file.s3KeyName, index, file.originalFileName, file.isPresigned)}
                                style={{ marginRight: '1rem', textDecoration: 'underline' }}
                                className="clickable"
                            >
                                DOWNLOAD
                            </span>
                            <span onClick={() => deleteFile(file.id)} style={{ color: 'red' }} className="clickable">X</span>
                            {/* <a onClick={() => downloadFile(file.s3KeyName, index, file.originalFileName)}>
                                <i style={{ fontSize: '1.5rem' }} className="far fa-download mr-1 clickable" />
                            </a>

                            <i
                                style={{ fontSize: '1.5rem' }}
                                className="far fa-eye mr-1 clickable"
                                onClick={() => dispatch(actions.layout.showDialog(DialogList.DOCUMENT_VIEWER))}
                            />
                            <a onClick={() => deleteFile(file.id)} style={{ color: 'red' }}>
                                <i style={{ fontSize: '1.5rem' }} className="far fa-trash mr-1 clickable" />
                            </a>
                            <i style={{ fontSize: '1.5rem' }} className="far fa-info-circle clickable" /> */}
                        </div>
                        {/* <button onClick={() => downloadFile(file.s3KeyName, index, file.originalFileName)}>download</button> */}
                        <a id={`a_${index}`} />
                    </div>
                );
            });
        } else {
            return <div>No Files Found</div>;
        }
    };

    const filesUploaded = () => {
        // props.hideDialog();
        dispatch(actions.layout.hideDialog());
        loadData();
        setRefreshFileList(true);
    };

    const uploadFileCallback = (files: File[]) => {
        setFilesToUpload(files);
    };

    // TODO: Delete?
    // const merDocs = async () => {
    //     const result = await post(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/documentTemplate/${appContext.leaseId}/merge/4`);

    //     if (response.ok) {
    //         // window.alert('Document Generated Successfully');

    //         await response.blob().then((blob) => {
    //             const blobUrl = window.URL.createObjectURL(blob);
    //             const a = document.getElementById(`a_mergedDoc`) as HTMLAnchorElement;
    //             console.log('testytestface', blob.size, blobUrl, a);
    //             a.href = blobUrl;
    //             a.download = 'MergedMailDoc';
    //             a.click();
    //             window.URL.revokeObjectURL(blobUrl);
    //         });
    //     } else {
    //         console.log('Unable to get Lease Clause Types', result);

    //     }
    // };

    return (
        <>
            <a style={{ display: 'hidden' }} id="refreshMORReport" />
            <div className="mb-1" style={{ margin: 'auto 2rem' }}>
                <RHFLegend title="Documents" />
                <div style={{ display: 'flex', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <div className="mr-2" style={{ marginBottom: '4rem' }}>
                            <div style={{ fontSize: '1.5rem', textAlign: 'center' }}>Attached Files</div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <div style={{ width: '10%', textAlign: 'center', fontSize: '1.35rem' }}>Upload Date</div>
                                <div style={{ width: '30%', textAlign: 'center', fontSize: '1.35rem' }}>Filename</div>
                                <div style={{ width: '10%', textAlign: 'center', fontSize: '1.35rem' }}>File Size</div>
                                <div style={{ width: '10%', textAlign: 'center', fontSize: '1.35rem' }}>Doc Type</div>
                                <div style={{ width: '25%', textAlign: 'center', fontSize: '1.35rem' }}>Note</div>
                                <div style={{ width: '15%', textAlign: 'center', fontSize: '1.35rem' }}>Actions</div>
                            </div>
                            <div style={{ maxHeight: '30rem', overflow: 'auto' }}>{displayFiles()}</div>
                        </div>
                    </div>
                </div>
                <div style={{ margin: '3rem 1rem' }}>
                    <Button
                        className="blue-hover-button"
                        style={{ marginRight: '1rem' }}
                        text="Attach Document"
                        onClick={() => dispatch(actions.layout.showDialog(DialogList.DOCUMENT_MANAGEMENT))}
                        disabled={props?.isNewLease}
                    />
                </div>
                <div>
                    <RHFLegend title="Lease Packet Management" />
                    <div className="mr-2">
                        <GenerateDocs
                            leaseFunction={props.generateLease}
                            lprFunction={props.generateLPR}
                            memoFunction={props.generateMemo}
                            oopFunction={props.generateOop}
                            isNewLease={props?.isNewLease}
                            contractType={props.contextType}
                            leaseId={LandContractType.BrineLease ? appContext.brineLeaseId : appContext.leaseId}
                            uploadReportFileCallback={uploadFileCallback}
                            updateOlderVersions={props.updateOlderVersions}
                            leaseNumber={props?.leaseNumber}
                        />
                    </div>

                    <div style={{ margin: '3rem 1rem' }}>
                        <Button
                            className="blue-hover-button"
                            text={loadingData ? 'Loading Documents...' : 'Build Lease Packet'}
                            onClick={getLeasePacket}
                            disabled={props?.isNewLease || loadingData}
                        />
                        <Button
                            className="blue-hover-button"
                            text={loadingData ? 'Loading Documents...' : 'Refresh MORs'}
                            onClick={props.refreshMORs}
                            disabled={props?.isNewLease || loadingData}
                            style={{ transform: 'translateX(20%)' }}
                        />
                    </div>

                    <div style={{ margin: '3rem 1rem' }}>
                        {/* <Button
                            text={loadingData ? 'Loading Documents...' : 'Mail Merge'}
                            size="medium"
                            onClick={merDocs}
                            disabled={props?.isNewLease || loadingData}
                        /> */}
                        <a style={{ display: 'hidden' }} id="a_mergedDoc" />
                    </div>
                </div>
            </div>
            <DocumentManagement
                filesUploaded={filesUploaded}
                context={docContext}
                contractLookupId={props.contractLookupId}
                filesToUpload={filesToUpload}
                tractMORfiles={tractFilesToUpload}
            />
            <DocumentViewer />
            <LeasePacketBuilder fileData={leaseFileData} landType={props.contextType} contractLookupId={props.contractLookupId} loadData={loadData} />
            <ConfirmDeleteDocDialog confirmDelete={deleteFileConfirmed} cancelDelete={deleteFileCanceled} />
        </>
    );
};

export default AgreementDocs;



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/AgreementDocs.tsx